import { css } from '@compiled/react';
import { formatDistanceToNow } from 'date-fns';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Heading from '@atlaskit/heading';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';

import { Link as RouterLink } from '@confluence/route-manager/entry-points/Link.ts';

import { SpaceLink } from './SpaceLink.tsx';
import type { Topic } from './QuestionTopics.tsx';
import { QuestionTopics } from './QuestionTopics.tsx';

const i18n = defineMessages({
	answerCount: {
		id: 'questions.question-item.answer-count',
		defaultMessage: '{count, plural, one {{count} answer} other {{count} answers}}',
		description: 'Count of answers for a question',
	},
	voteCount: {
		id: 'questions.question-item.vote-count',
		defaultMessage: '{count, plural, one {vote} other {votes}}',
		description: 'Count of votes for a question',
	},
});

type QuestionItemProps = {
	questionTitle: string;
	questionId: string;
	topics?: Array<Topic>;
	numberOfVotes: number;
	numberOfAnswers: number;
	createdBy: string;
	createdDate: Date;
	routerName: string;
	spaceKey?: string;
	spaceName?: string;
	hasAcceptedAnswer?: boolean;
};

const outerStyles = xcss({ marginBlock: 'space.100' });

const linkStyles = css({
	textDecoration: 'none',
});

const voteCountStyles = xcss({
	textAlign: 'center',
	minWidth: '32px',
});

export const QuestionItem: React.FC<QuestionItemProps> = ({
	questionTitle,
	questionId,
	topics,
	routerName,
	numberOfAnswers,
	createdBy,
	numberOfVotes,
	createdDate,
	spaceKey,
	spaceName,
	hasAcceptedAnswer,
}) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={outerStyles}>
			<Inline space="space.200">
				<Stack alignInline="center" xcss={voteCountStyles}>
					<Heading size="medium">{numberOfVotes}</Heading>
					<Text size="small">
						{formatMessage(i18n.voteCount, {
							count: numberOfVotes,
						})}
					</Text>
				</Stack>
				<Stack alignBlock="start" space="space.100">
					<Heading size="small">
						<Inline alignBlock="center" space="space.100">
							{hasAcceptedAnswer && (
								<CheckCircleIcon color="var(--ds-icon-accent-green)" label="Accepted answer" />
							)}
							<RouterLink css={linkStyles} name={routerName} params={{ questionId, spaceKey }}>
								{questionTitle}
							</RouterLink>
						</Inline>
					</Heading>
					<Inline alignBlock="center" shouldWrap space="space.100" separator="·">
						<Text size="small">
							{formatMessage(i18n.answerCount, {
								count: numberOfAnswers,
							})}
						</Text>
						<Text size="small">{createdBy}</Text>
						<Text size="small">{formatDistanceToNow(createdDate, { addSuffix: true })}</Text>
						{spaceKey && spaceName && <SpaceLink spaceKey={spaceKey} spaceName={spaceName} />}
					</Inline>
					<QuestionTopics topics={topics} />
				</Stack>
			</Inline>
		</Box>
	);
};
